import { Link } from "react-router-dom";
import { Cookie, Settings2, ArrowRight, ExternalLink } from "lucide-react";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { openPreferences } from "@/lib/cookieConsent";

const COOKIES = [
  {
    nombre: "ldc_session",
    proveedor: "Ley de Datos Chile (primera parte)",
    proposito: "Mantener tu sesión iniciada en /cuenta y /admin.",
    duracion: "Sesión (se borra al cerrar el navegador) + token de 7 días",
    categoria: "Necesaria",
    transferencia: "Chile (servidor propio)",
  },
  {
    nombre: "csrftoken / Authorization header",
    proveedor: "Ley de Datos Chile (primera parte)",
    proposito: "Protección contra ataques CSRF y autenticación JWT.",
    duracion: "Sesión",
    categoria: "Necesaria",
    transferencia: "Chile",
  },
  {
    nombre: "ldc_cookie_consent_v1",
    proveedor: "Ley de Datos Chile (primera parte)",
    proposito: "Recordar tu decisión sobre el banner de cookies. Registro de consentimiento exigido por Art. 12 Ley 21.719.",
    duracion: "12 meses · localStorage",
    categoria: "Necesaria",
    transferencia: "Chile (no sale del navegador)",
  },
  {
    nombre: "ph_phc_… / ph_distinct_id",
    proveedor: "PostHog (PostHog Inc., EE.UU.)",
    proposito: "Métricas de uso anónimas, eventos de producto y session recording (interacciones, scroll, clicks). El contenido tecleado en formularios se enmascara automáticamente.",
    duracion: "12 meses",
    categoria: "Analítica · opt-in",
    transferencia: "EE.UU. — bajo Cláusulas Contractuales Modelo aprobadas por el Min. Economía de Chile (RAEX 2025-03731)",
  },
  {
    nombre: "_ga, _ga_SXBBK8BQJR",
    proveedor: "Google Analytics 4 (Google LLC, EE.UU.)",
    proposito: "Distinguir usuarios y sesiones de manera anónima, medir embudos de conversión (escaneo → lead → checkout) y reportar uso agregado del producto. Configurado con IP anonimizada (anonymize_ip: true).",
    duracion: "_ga: 24 meses · _ga_SXBBK8BQJR: 24 meses",
    categoria: "Analítica · opt-in",
    transferencia: "EE.UU. — bajo Cláusulas Contractuales Modelo aprobadas por el Min. Economía de Chile (RAEX 2025-03731)",
  },
];

const NO_USAMOS = [
  "Meta Pixel (Facebook / Instagram)",
  "TikTok Pixel",
  "Google Ads / Floodlight",
  "LinkedIn Insight Tag",
  "Twitter Universal Tag",
  "Hotjar / Microsoft Clarity",
  "Cookies de retargeting de terceros",
];

export default function PoliticaCookies() {
  return (
    <>
      <SEO
        title="Política de Cookies — Ley de Datos Chile"
        description="Cookies y herramientas de análisis que usa Ley de Datos Chile. Listado completo conforme a la Ley N° 21.719: nombre, propósito, vida útil, vendor y transferencias internacionales."
        path="/politica-cookies"
        keywords="política de cookies Chile, Ley 21.719 cookies, consentimiento previo, PostHog, transferencia internacional"
        schemas={[
          orgSchema,
          breadcrumbSchema([
            { name: "Inicio", path: "/" },
            { name: "Política de Cookies", path: "/politica-cookies" },
          ]),
        ]}
      />
      <section className="px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E3A8A]/5 text-[#1E3A8A] text-xs font-semibold uppercase tracking-wide mb-6">
            <Cookie className="w-3.5 h-3.5" />
            Política de Cookies · Ley 21.719
          </div>
          <h1
            data-testid="cookies-title"
            className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900"
          >
            Qué cookies usamos y cómo controlas las opcionales
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Esta política describe en detalle cada cookie y herramienta de medición que usa
            <strong> leydedatoschile.com</strong>. Solo cargamos cookies opcionales después de
            tu consentimiento explícito — cumpliendo el Art. 12 de la Ley N° 21.719.
            <strong className="text-slate-900"> Versión 1.0 — 22 de febrero de 2026.</strong>
          </p>

          <button
            type="button"
            onClick={openPreferences}
            data-testid="cookies-open-preferences"
            className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm font-semibold transition-colors"
          >
            <Settings2 className="w-4 h-4" />
            Cambiar mis preferencias de cookies
          </button>

          <h2 className="font-display text-2xl font-semibold text-slate-900 mt-14 mb-3">
            1. Cookies que sí usamos
          </h2>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="cookies-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Nombre</th>
                  <th className="text-left px-4 py-3 font-semibold">Propósito</th>
                  <th className="text-left px-4 py-3 font-semibold">Duración</th>
                  <th className="text-left px-4 py-3 font-semibold">Categoría</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {COOKIES.map((c) => (
                  <tr key={c.nombre} className="align-top">
                    <td className="px-4 py-3 font-mono text-xs text-slate-900 whitespace-nowrap">
                      <div>{c.nombre}</div>
                      <div className="text-[10px] text-slate-500 mt-1">{c.proveedor}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-md">{c.proposito}<div className="text-[11px] text-slate-500 mt-1.5">Transferencia: {c.transferencia}</div></td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">{c.duracion}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={
                        c.categoria === "Necesaria"
                          ? "inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase tracking-wider"
                          : "inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold uppercase tracking-wider"
                      }>
                        {c.categoria}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="font-display text-2xl font-semibold text-slate-900 mt-14 mb-3">
            2. Cookies que NO usamos
          </h2>
          <p className="text-slate-600">
            Para que sepas exactamente con qué te enfrentás, declaramos explícitamente lo que
            <strong> no</strong> está en nuestro sitio:
          </p>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {NO_USAMOS.map((x) => (
              <li
                key={x}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 text-sm text-slate-700"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400" aria-hidden="true" />
                {x}
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500 mt-3">
            Si en el futuro agregamos alguna herramienta de marketing, te lo informaremos
            previamente y te pediremos un nuevo consentimiento explícito antes de cargarla.
          </p>

          <h2 className="font-display text-2xl font-semibold text-slate-900 mt-14 mb-3">
            3. Cómo controlar las cookies opcionales
          </h2>
          <ul className="text-slate-700 space-y-2 list-disc pl-5">
            <li>
              Usá el botón <strong>Cambiar mis preferencias de cookies</strong> arriba para
              activar o desactivar analíticas en cualquier momento.
            </li>
            <li>
              También puedes bloquear cookies desde tu navegador: Chrome, Firefox, Safari y
              Edge tienen controles granulares en Configuración → Privacidad.
            </li>
            <li>
              Tu decisión se guarda en <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">localStorage</code> por 12 meses. Tras ese plazo, te volveremos a preguntar.
            </li>
          </ul>

          <div className="mt-10 p-5 rounded-2xl bg-blue-50 border border-blue-200">
            <h3 className="font-display text-base font-semibold text-blue-900 mb-1.5">
              ¿Buscás cómo darte de baja de los emails educativos?
            </h3>
            <p className="text-sm text-blue-900/85 leading-relaxed">
              Los <strong>3 emails educativos</strong> que enviamos opcionalmente tras un scan{" "}
              <strong>no son cookies</strong>: son emails con tu consentimiento explícito.
              Cada email incluye un link <em>&ldquo;Darme de baja&rdquo;</em> y soportamos
              {" "}<code className="text-xs bg-white px-1.5 py-0.5 rounded border border-blue-200">List-Unsubscribe</code>{" "}
              (un clic desde tu cliente de email). Detalles completos en la{" "}
              <Link to="/politica-privacidad" className="underline font-semibold">
                sección 14 de la Política de Privacidad
              </Link>
              .
            </p>
          </div>

          <h2 className="font-display text-2xl font-semibold text-slate-900 mt-14 mb-3">
            4. Transferencia internacional de datos (PostHog · Google Analytics)
          </h2>
          <p className="text-slate-700">
            PostHog y Google Analytics procesan los eventos analíticos en servidores ubicados en{" "}
            <strong>EE.UU.</strong> Estas transferencias se realizan bajo las{" "}
            <a
              href="https://www.economia.gob.cl/2025/12/10/raex202503731-aprueba-las-clausulas-contractuales-modelo-para-transferencias-internacionales-que-indica.htm"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1E3A8A] underline hover:text-[#1E40AF] inline-flex items-center gap-1"
            >
              Cláusulas Contractuales Modelo aprobadas por el Ministerio de Economía
              <ExternalLink className="w-3 h-3" />
            </a>
            {" "}(RAEX 2025-03731, diciembre 2025). Google Analytics está configurado con{" "}
            <strong>IP anonimizada</strong> (<code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">anonymize_ip: true</code>) y PostHog enmascara automáticamente el contenido tecleado en formularios.
          </p>

          <h2 className="font-display text-2xl font-semibold text-slate-900 mt-14 mb-3">
            5. Cambios en esta política
          </h2>
          <p className="text-slate-700">
            Si modificamos qué cookies usamos o agregamos nuevas herramientas, actualizaremos
            esta página y reiniciaremos el banner de consentimiento. Tu decisión previa quedará
            invalidada y deberás otorgar consentimiento nuevamente.
          </p>

          <div className="mt-14 p-7 rounded-2xl bg-slate-50 border border-slate-200">
            <h3 className="font-display text-xl font-semibold text-slate-900">
              ¿Tu sitio también necesita una política de cookies así?
            </h3>
            <p className="mt-2 text-slate-600">
              El Módulo Pro incluye un banner de cookies HTML autocontenido + política de
              cookies generada con tus datos.
            </p>
            <Link
              to="/modulo-pro"
              className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold transition-colors"
            >
              Conocer el Módulo Pro
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
